import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PenLine, Plus } from 'lucide-react'

import { deleteWriterScriptDocument, listWriterScriptDocuments, WRITER_STUDIO_BASE } from '@/api/studio-writer'
import type { ScriptDocument } from '@/api/studio-production'
import { AuthSessionExpiredError } from '@/api/authSession'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar, CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { PageEmptyState, PageEmptyStateActionButton } from '@/shared/components/PageEmptyState'
import { WriterScriptDocumentModal } from '@/features/studio/modals/WriterScriptDocumentModal'
import { ScriptDocumentCard } from '@/features/studio/components/ScriptDocumentCard'
import { StudioHubPageShell } from '@/features/studio/components/StudioHubPageShell'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  ProtoPagination,
  ScriptDeleteBtn,
  ScriptMetaEditBtn,
  ScriptWriteLink,
} from '@/features/studio/components/ui-kit'

const PAGE_SIZE = 12

export function WriterScriptListPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [items, setItems] = useState<ScriptDocument[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ScriptDocument | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [metaOpen, setMetaOpen] = useState(false)
  const [metaEditing, setMetaEditing] = useState<ScriptDocument | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    const offset = (page - 1) * PAGE_SIZE
    try {
      const res = await listWriterScriptDocuments(PAGE_SIZE, offset)
      setItems(res.items ?? [])
      setTotal(res.total ?? 0)
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setError(resolveApiErrorMessage(e, 'writer.script.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
    if (page > maxPage) setPage(maxPage)
  }, [total, page])

  function openMetaModal(doc: ScriptDocument | null) {
    setMetaEditing(doc)
    setMetaOpen(true)
  }

  function openEditor(scriptId: number) {
    navigate(`${WRITER_STUDIO_BASE}/script/${scriptId}/episodes/1`)
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      await deleteWriterScriptDocument(deleteTarget.id)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setDeleteError(resolveApiErrorMessage(e, 'writer.script.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <>
      <StudioHubPageShell mode="writer">
        <CatalogListToolbar
          actions={
            <CatalogListPrimaryButton disabled={loading} onClick={() => openMetaModal(null)}>
              <Plus className="h-4 w-4" />
              {t('writer.script.create')}
            </CatalogListPrimaryButton>
          }
        />

        {error ? (
          <p className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-300" role="alert">
            {t(error, { defaultValue: error })}
          </p>
        ) : null}

        {loading ? (
          <CatalogListSkeleton />
        ) : total === 0 ? (
          <PageEmptyState icon={PenLine} tone="amber" title={t('writer.script.emptyTitle')} subtitle={t('writer.script.empty')}>
            <PageEmptyStateActionButton tone="amber" onClick={() => openMetaModal(null)}>
              <Plus className="h-4 w-4" />
              {t('writer.script.createFirst')}
            </PageEmptyStateActionButton>
          </PageEmptyState>
        ) : (
          <>
            <ul className={CATALOG_LIST_GRID_CLASS}>
            {items.map((doc, rowIndex) => {
              const globalIndex = (page - 1) * PAGE_SIZE + rowIndex
              return (
                <ScriptDocumentCard
                  key={doc.id}
                  doc={doc}
                  index={globalIndex}
                  href={`${WRITER_STUDIO_BASE}/script/${doc.id}/episodes/1`}
                  bottomActions={
                    <>
                      <ScriptMetaEditBtn onClick={() => openMetaModal(doc)}>{t('script.editMeta')}</ScriptMetaEditBtn>
                      <ScriptWriteLink to={`${WRITER_STUDIO_BASE}/script/${doc.id}/episodes/1`}>
                        {t('script.writeScript')}
                      </ScriptWriteLink>
                      <ScriptDeleteBtn iconOnly aria-label={t('common.delete')} onClick={() => setDeleteTarget(doc)} />
                    </>
                  }
                />
              )
            })}
          </ul>
          {total > PAGE_SIZE ? (
            <ProtoPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          ) : null}
        </>
      )}
      </StudioHubPageShell>

      <WriterScriptDocumentModal
        open={metaOpen}
        onClose={() => {
          setMetaOpen(false)
          setMetaEditing(null)
        }}
        editing={metaEditing}
        onSaved={() => void reload()}
        onWrite={openEditor}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('writer.script.delete')}
        message={t('writer.script.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (deleteBusy) return
          setDeleteTarget(null)
          setDeleteError(null)
        }}
      />
    </>
  )
}
